import { useState, useMemo } from 'react';
import { useItems } from '../hooks';
import {
  SearchBar,
  CategoryFilters,
  ItemCard,
  LoadingSpinner,
  EmptyState,
} from '../components';
import { Search } from 'lucide-react';

export function ItemList() {
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState('');

  const filters = useMemo(
    () => ({
      search,
      category,
      minPrice: null,
      maxPrice: null,
      tags: [],
    }),
    [search, category]
  );

  const { items, loading, error } = useItems(filters);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Items</h1>
        <p className="text-gray-500 dark:text-gray-400 mt-1">
          Browse and search household items with price tiers
        </p>
      </div>

      {/* Search & Filters */}
      <div className="space-y-4">
        <SearchBar
          value={search}
          onChange={setSearch}
          placeholder="Search items by name, category, or tags..."
        />
        <CategoryFilters selected={category} onChange={setCategory} />
      </div>

      {/* Error State */}
      {error && (
        <div className="bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded-lg p-4 text-red-700 dark:text-red-400">
          <p className="font-medium">Error loading items</p>
          <p className="text-sm">{error}</p>
        </div>
      )}

      {/* Loading State */}
      {loading && (
        <div className="flex items-center justify-center py-12">
          <LoadingSpinner text="Loading items..." />
        </div>
      )}

      {/* Empty State */}
      {!loading && items.length === 0 && (
        <EmptyState
          title="No items found"
          description={
            search || category
              ? 'Try adjusting your search or filters'
              : 'Add some items to get started'
          }
          icon={Search}
        />
      )}

      {/* Items Grid */}
      {!loading && items.length > 0 && (
        <>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            Showing {items.length} item{items.length !== 1 ? 's' : ''}
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {items.map((item) => (
              <ItemCard key={item.id} item={item} />
            ))}
          </div>
        </>
      )}
    </div>
  );
}
